import * as React from "react"

import type {
  ToastActionElement,
  ToastProps,
} from "@/components/ui/toast"

const TOAST_LIMIT = 1
const TOAST_REMOVE_DELAY = 1000000

// Auto-hide timeouts for different toast types
const NORMAL_TOAST_DURATION = 2000 // 2 seconds for normal toasts
const ERROR_TOAST_DURATION = 4000 // 4 seconds for error toasts

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
  duration?: number
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: ToasterToast["id"]
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: ToasterToast["id"]
    }

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()
const autoDismissTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

const addToAutoDismissQueue = (toastId: string, duration: number) => {
  if (autoDismissTimeouts.has(toastId)) {
    clearTimeout(autoDismissTimeouts.get(toastId))
  }

  const timeout = setTimeout(() => {
    autoDismissTimeouts.delete(toastId)
    dispatch({
      type: "DISMISS_TOAST",
      toastId: toastId,
    })
  }, duration)

  autoDismissTimeouts.set(toastId, timeout)
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case "DISMISS_TOAST": {
      const { toastId } = action

      // Clean up auto-dismiss timeouts
      if (toastId) {
        if (autoDismissTimeouts.has(toastId)) {
          clearTimeout(autoDismissTimeouts.get(toastId))
          autoDismissTimeouts.delete(toastId)
        }
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach((toast) => {
          if (autoDismissTimeouts.has(toast.id)) {
            clearTimeout(autoDismissTimeouts.get(toast.id))
            autoDismissTimeouts.delete(toast.id)
          }
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      }
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        // Clean up all auto-dismiss timeouts
        autoDismissTimeouts.forEach((timeout) => clearTimeout(timeout))
        autoDismissTimeouts.clear()
        return {
          ...state,
          toasts: [],
        }
      }
      
      // Clean up specific auto-dismiss timeout
      if (autoDismissTimeouts.has(action.toastId)) {
        clearTimeout(autoDismissTimeouts.get(action.toastId))
        autoDismissTimeouts.delete(action.toastId)
      }
      
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

type Toast = Omit<ToasterToast, "id">

function toast({ ...props }: Toast) {
  const id = genId()

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    })
  const dismiss = () => {
    // Clear auto-dismiss timeout when manually dismissed
    if (autoDismissTimeouts.has(id)) {
      clearTimeout(autoDismissTimeouts.get(id))
      autoDismissTimeouts.delete(id)
    }
    dispatch({ type: "DISMISS_TOAST", toastId: id })
  }

  // Determine auto-hide duration based on variant
  let autoDismissDuration: number
  if (props.variant === "destructive") {
    autoDismissDuration = ERROR_TOAST_DURATION
  } else {
    autoDismissDuration = NORMAL_TOAST_DURATION
  }

  // Use custom duration if provided, otherwise use auto-determined duration
  const finalDuration = props.duration !== undefined ? props.duration : autoDismissDuration

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      duration: finalDuration,
      onOpenChange: (open) => {
        if (!open) dismiss()
      },
    },
  })

  // Set up auto-dismiss
  addToAutoDismissQueue(id, finalDuration)

  return {
    id: id,
    dismiss,
    update,
  }
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [state])

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
  }
}

export { useToast, toast }
